import React from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { TiHeader } from './TiTabs';
import type { TiTabId } from './TiTabs';
import { TC_NUMBERS, TC_OPPORTUNITY } from '../../treatment/mockData';
import type { TcBreakdownRow, TcFunnelRow } from '../../treatment/types';

export interface TiOpportunityViewProps {
  onSelectTab?: (tab: TiTabId) => void;
  onOpenSettings?: () => void;
  onNewCampaign?: () => void;
  /** Opens the Calls section filtered to Treatment (spec 31 §7). */
  onOpenCalls?: () => void;
  /** Start a campaign with a description already written (§3). */
  onStartCampaign?: (description: string) => void;
}

/**
 * Opportunity (amendment 27 §3, board `TC2Opportunity`).
 *
 * The hero is a **subtraction** — the gross diagnosed figure down to *Reachable
 * now*, so the small credible number is visibly derived from the big one. Every
 * breakdown row starts a campaign with its description filled in.
 *
 * Removed: the *Held by the practice* row (only *On hold in the PMS* stays), the
 * *What it takes to close* column, *Thresholds →*, the AI-Team link, and
 * *Scheduled in 30 days*.
 */
export function TiOpportunityView({
  onSelectTab,
  onOpenSettings,
  onNewCampaign,
  onOpenCalls,
  onStartCampaign,
}: TiOpportunityViewProps) {
  const o = TC_OPPORTUNITY;
  const reachable = o.funnel.find((r) => r.kind === 'total');

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1180px] flex-1 overflow-y-auto px-8 pb-16 pt-6">
      <TiHeader
        active="opportunity"
        eyebrow={`${String(TC_NUMBERS.reachable)} REACHABLE · ${TC_NUMBERS.reachableValue} OF TREATMENT`}
        onSelectTab={onSelectTab}
        onOpenSettings={onOpenSettings}
        onNewCampaign={onNewCampaign}
        onOpenCalls={onOpenCalls}
      />

      <div className="mt-5 grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        {/* the subtraction */}
        <div className="rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">
            From everything diagnosed to what you can work
          </div>
          <div className="mt-3 grid grid-cols-[1fr_0.5fr_0.6fr] gap-x-3 pb-1 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.07em] text-[var(--text-muted)]">
            <span />
            <span className="text-right">Patients</span>
            <span className="text-right">Value</span>
          </div>
          {o.funnel.map((r) => (
            <FunnelRow key={r.key} row={r} />
          ))}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-default)] pt-3">
            <span className="text-[12px] text-[var(--text-muted)]">Average case {o.averageCase}</span>
            <Button
              variant="primary"
              size="sm"
              onClick={() => { onStartCampaign?.(`All ${String(reachable?.patients ?? TC_NUMBERS.reachable)} reachable patients with diagnosed treatment`); }}
            >
              Start a campaign with all {reachable?.patients ?? TC_NUMBERS.reachable}
            </Button>
          </div>
        </div>

        {/* three side tiles */}
        <div className="grid gap-3">
          <SideTile eyebrow="Booked through TC · last 30 days" value={TC_NUMBERS.bookedValue} sub={`${String(TC_NUMBERS.booked30)} patients · ${TC_NUMBERS.completedValue} completed so far`} />
          <SideTile eyebrow="Acceptance" value={`${String(TC_NUMBERS.acceptancePct)}%`} sub={`booked within 60 days of diagnosis · was ${String(TC_NUMBERS.acceptanceWas)}% before ELVA`} />
          <SideTile eyebrow="Diagnosed this month" value={o.diagnosedThisMonth} sub={`${String(o.diagnosedThisMonthCases)} new cases, not booked yet`} />
        </div>
      </div>

      {/* 01 by case size */}
      <Card className="mt-4">
        <SectionHeading n="01" title="By the size of the case" sub="What each patient’s treatment costs in total" />
        <div className="grid grid-cols-[1.2fr_0.6fr_0.7fr_0.8fr_1fr] gap-x-3 px-1 pb-1 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.07em] text-[var(--text-muted)]">
          <span>Case value</span><span className="text-right">Patients</span><span className="text-right">Value</span><span className="text-right">Booked ≤60d</span><span />
        </div>
        {o.caseBands.map((b) => (
          <BreakdownRow key={b.key} row={b} withBooked onStart={() => { onStartCampaign?.(b.campaignDescription); }} />
        ))}
      </Card>

      {/* 02 who diagnosed it */}
      <Card className="mt-4">
        <SectionHeading n="02" title="Who diagnosed it" sub="" />
        {o.providers.map((b) => (
          <BreakdownRow key={b.key} row={b} onStart={() => { onStartCampaign?.(b.campaignDescription); }} />
        ))}
      </Card>

      {/* 03 what it is */}
      <Card className="mt-4">
        <SectionHeading n="03" title="What it is" sub="" />
        {o.procedures.map((b) => (
          <BreakdownRow key={b.key} row={b} onStart={() => { onStartCampaign?.(b.campaignDescription); }} />
        ))}
      </Card>
    </div>
  );
}

function FunnelRow({ row }: { row: TcFunnelRow }) {
  const total = row.kind === 'total';
  return (
    <div
      className={cn(
        'grid grid-cols-[1fr_0.5fr_0.6fr] items-baseline gap-x-3 px-1 py-2',
        total ? 'mt-1 rounded-[8px] bg-[#E8F3EC]' : 'border-t border-[var(--border-default)]',
      )}
    >
      <span className="min-w-0">
        <span className={cn('block text-[12.5px]', total ? 'font-bold text-[var(--money-green)]' : 'text-[var(--text-primary)]')}>
          {row.kind === 'deduction' ? '− ' : ''}{row.label}
        </span>
        {row.note !== undefined ? <span className="block text-[11px] text-[var(--text-muted)]">{row.note}</span> : null}
      </span>
      <span className={cn('text-right font-[var(--font-mono)] text-[12.5px]', total ? 'font-bold text-[var(--money-green)]' : 'text-[var(--text-secondary)]')}>
        {row.patients.toLocaleString('en-US')}
      </span>
      <span className={cn('text-right font-[var(--font-mono)] text-[12.5px] font-bold', total ? 'text-[var(--money-green)]' : 'text-[var(--text-primary)]')}>
        {row.value}
      </span>
    </div>
  );
}

function BreakdownRow({ row, withBooked, onStart }: { row: TcBreakdownRow; withBooked?: boolean; onStart: () => void }) {
  return (
    <div
      className={cn(
        'grid items-center gap-x-3 border-t border-[var(--border-default)] px-1 py-2 text-[12.5px]',
        withBooked === true ? 'grid-cols-[1.2fr_0.6fr_0.7fr_0.8fr_1fr]' : 'grid-cols-[1.2fr_0.6fr_0.7fr_1fr]',
      )}
    >
      <span className="font-semibold text-[var(--text-primary)]">{row.label}</span>
      <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{row.patients}</span>
      <span className="text-right font-[var(--font-mono)] font-bold text-[var(--text-primary)]">{row.value}</span>
      {withBooked === true ? <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{row.bookedPct}</span> : null}
      <span className="text-right">
        <button type="button" onClick={onStart} className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">
          Start a campaign →
        </button>
      </span>
    </div>
  );
}

function SideTile({ eyebrow, value, sub }: { eyebrow: string; value: string; sub: string }) {
  return (
    <div className="rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{eyebrow}</div>
      <div className="mt-1 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.03em] text-[var(--text-primary)]">{value}</div>
      <div className="mt-0.5 text-[11.5px] leading-[1.5] text-[var(--text-muted)]">{sub}</div>
    </div>
  );
}

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]', className)}>
      {children}
    </div>
  );
}

function SectionHeading({ n, title, sub }: { n: string; title: string; sub: string }) {
  return (
    <div className="mb-2 flex flex-wrap items-baseline gap-2.5">
      <span className="font-[var(--font-mono)] text-[11px] font-bold text-[var(--text-muted)]">{n}</span>
      <h2 className="m-0 text-[14px] font-bold text-[var(--text-primary)]">{title}</h2>
      {sub !== '' ? <span className="text-[12px] text-[var(--text-muted)]">{sub}</span> : null}
    </div>
  );
}
